/**
 * coverage.ts
 * Pure helper for computing per-employee skill coverage in the Skill Matrix.
 *
 * Coverage = cells where Level >= 2 ("Training is ongoing" or better)
 *            / total visible task types (respects group collapse + task filter).
 *
 * "Visible" task types are those NOT in a collapsed category group and NOT
 * filtered out by the toolbar task-type filter. The caller derives the
 * visibleSkillKeys Set from the rendered columns and passes it in; this
 * function has no knowledge of group/filter state itself.
 *
 * The function is designed to be called per-row during the render pass, so
 * it must be O(visibleSkillKeys.size) not O(cells.length) for the inner
 * loop.
 */

import type { SkillMatrixCell } from '../../../../services/scheduleSkillMatrix';

export interface CoverageResult {
  numerator: number;
  denominator: number;
  /** 0–100, rounded to nearest integer. */
  percent: number;
}

/**
 * Compute skill coverage for a single employee against a set of visible
 * task type keys.
 *
 * @param cells            Full flat cell array from the store (all employees).
 * @param userKey          The employee whose row we are computing.
 * @param visibleSkillKeys  Set of SkillKey strings that are currently
 *                         rendered (collapsed groups and filtered-out types
 *                         should already be excluded by the caller).
 */
export function computeCoverage(
  cells: SkillMatrixCell[],
  userKey: string,
  visibleSkillKeys: Set<string>,
): CoverageResult {
  const denominator = visibleSkillKeys.size;
  if (denominator === 0) {
    return { numerator: 0, denominator: 0, percent: 0 };
  }

  // Build a fast lookup of level by SkillKey for this user.
  // We only inspect cells that belong to this user.
  const levelByTask = new Map<string, number | null>();
  for (const cell of cells) {
    if (cell.UserKey === userKey) {
      levelByTask.set(cell.SkillKey, cell.Level);
    }
  }

  let numerator = 0;
  for (const ttKey of visibleSkillKeys) {
    const level = levelByTask.get(ttKey) ?? null;
    if (level !== null && level >= 2) {
      numerator++;
    }
  }

  const percent = Math.round((numerator / denominator) * 100);
  return { numerator, denominator, percent };
}
